import { useEffect, useMemo } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useSetAtom } from "jotai";
import { Path, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NodeInformation } from "../../../../..";
import { AddIcon, CloseIcon, DeleteIcon, SaveIcon } from "../../../../../../../../../../assets/icons";
import FormSelect from "../../../../../../../../../../components/forms/FormSelect";
import FormTextField from "../../../../../../../../../../components/forms/FormTextField";
import {
	SiteGoodsFlowDTO,
	SiteGoodsFlowItemDTO,
	SiteGoodsFlowPayload,
} from "../../../../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../../../../datasources/networking/apiRoutes";
import { GetOptionsContext, GetOptionsTopic } from "../../../../../../../../../../datasources/requests/common";
import { useServerOptions } from "../../../../../../../../../../hooks/useOptions";
import { useWriteResource } from "../../../../../../../../../../hooks/useResource";
import { UserPeriodFilter } from "../../../../../../../../../../state/company";
import { StorageKeys } from "../../../../../../../../../../state/storage";

type GoodsFlow = {
	year?: string;
	value: string;
};

type DialogFormValues = {
	measureId?: number;
	conversionFactor?: number;
	goodsFlow: GoodsFlow[];
};

type Props = {
	open: boolean;
	handleClose: VoidFunction;
	nodeInformation: NodeInformation;
	items: SiteGoodsFlowItemDTO[];
	onChangeItems: (items: SiteGoodsFlowItemDTO[]) => void;
};

const MeasureId = {
	KG: 4,
	TON: 5,
	M3: 6,
	PALLET: 7,
} as const;

export const GoodsFlowDialog = ({ open, handleClose, nodeInformation, items, onChangeItems }: Props) => {
	const { t } = useTranslation("dataCollection");

	const filterPeriod = localStorage.getItem(StorageKeys.PeriodFilter);
	const filterPeriodParsed = JSON.parse(filterPeriod ?? "") as UserPeriodFilter;
	const endYear = dayjs(filterPeriodParsed.end).year();

	// default form values
	const form = useForm<DialogFormValues>({
		defaultValues: {
			measureId: MeasureId.KG,
			conversionFactor: undefined,
			goodsFlow: [],
		},
		mode: "onChange",
	});

	// field dynamic array
	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "goodsFlow",
	});

	const { data: dataMeasures, isLoading } = useServerOptions(
		GetOptionsTopic.MeasureUnits,
		GetOptionsContext.GoodFlow
	);

	// define measure options based on server data
	const measureOptions = useMemo(() => {
		return (dataMeasures?.options || []).map((measure) => ({
			id: measure.id,
			label: measure.label,
		}));
	}, [dataMeasures?.options]);

	// validate measure ids from backend
	// protects frontend from invalid/legacy values
	const validMeasureIds = measureOptions.map((measure) => measure.id);

	// normalize backend measureId
	// fallback to KG if backend sends invalid value
	const normalizedMeasureId = validMeasureIds.includes(nodeInformation.goodsFlowMeasureId ?? -1)
		? nodeInformation.goodsFlowMeasureId
		: MeasureId.KG;

	// get measure label for current measureId
	const measureId = form.watch("measureId");

	// show conversion factor only if measure is not KG or TON
	const showConversionFactor = measureId !== MeasureId.KG && measureId !== MeasureId.TON;

	const measureLabel = measureOptions.find((measure) => measure.id === measureId)?.label ?? "";

	// inizialize form values when dialog opens
	// reset form when dialog is closed
	useEffect(() => {
		if (!open) {
			form.reset();
			return;
		}

		// wait for measure options before resetting form
		// avoids async issues with select values
		if (!measureOptions.length) {
			return;
		}

		form.reset({
			// use normalizedMeasureId instead of raw backend value
			measureId: normalizedMeasureId,
			conversionFactor: nodeInformation.goodsFlowConversionFactor ?? undefined,
			goodsFlow: items.map((i) => ({
				year: String(i.year ?? ""),
				value: String(i.value?.value ?? ""),
			})),
		});

		form.trigger();
	}, [open, items, measureOptions.length, normalizedMeasureId, nodeInformation.goodsFlowConversionFactor]);

	// reset conversion factor when measure changes to KG or TON
	useEffect(() => {
		if (!showConversionFactor) {
			form.setValue("conversionFactor", undefined, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	}, [showConversionFactor]);

	// validate year: required, 4 digit number, max year, no duplicates
	const validateYear = (value: string, index: number) => {
		const v = value?.trim();

		if (!v) return t("dialog good flow/required");
		if (!/^[1-9]\d{3}$/.test(v)) return t("dialog good flow/invalid year");

		const year = Number(v);
		if (year > endYear) return t("dialog good flow/max year", { maxYear: endYear });

		const duplicate = form.getValues("goodsFlow").some((g, i) => i !== index && g.year === v);

		if (duplicate) return t("dialog good flow/year already used");

		return true;
	};

	// function to map form values to payload
	const mapToPayload = (data: DialogFormValues): SiteGoodsFlowPayload => ({
		measureId: data.measureId,
		conversionFactor: data.conversionFactor,
		items: data.goodsFlow.map((g) => ({
			year: g.year ? Number(g.year) : undefined,
			value: g.value ? Number(g.value) : undefined,
		})),
	});

	const { put: updateGoodsFlow, isLoading: saving } = useWriteResource<SiteGoodsFlowPayload, SiteGoodsFlowDTO>({
		route: ApiRoutes.ConfigurationGoodFlows,
		path: { id: nodeInformation.id },
	});

	const handleSave = async (data: DialogFormValues) => {
		try {
			await updateGoodsFlow(mapToPayload(data));

			// update local state in parent component
			// with new values
			onChangeItems(
				data.goodsFlow.map((g) => ({
					year: g.year ? Number(g.year) : undefined,
					value: {
						value: g.value ? Number(g.value) : undefined,
						measure: measureLabel,
					},
				}))
			);

			handleClose();
		} catch (error) {
			console.error(error);
		}
	};

	const { isValid, isDirty } = form.formState;

	const canSave = isValid && isDirty;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
				<DialogTitle sx={{ p: 0 }}>{t("detail/manage goods flow")}</DialogTitle>

				<IconButton onClick={handleClose}>
					<CloseIcon />
				</IconButton>
			</Stack>

			<Divider sx={{ mx: 2 }} />

			<DialogContent sx={{ p: 2 }}>
				<Stack spacing={2}>
					<Stack direction="row" justifyContent="space-between">
						<Typography variant="subtitle2">{t("detail/units of measurement for flow rates")}</Typography>

						<Stack direction="row" spacing={2}>
							{showConversionFactor && (
								<Box sx={{ width: 100 }}>
									<FormTextField
										name="conversionFactor"
										form={form}
										label={t("dialog good flow/conversion factor")}
										isTypeNumber
										rules={{
											required: t("dialog good flow/required"),
											validate: (v) => Number(v) > 0 || "Must be > 0",
										}}
										isLoading={isLoading}
									/>
								</Box>
							)}

							<Box sx={{ width: 140 }}>
								<FormSelect
									name="measureId"
									form={form}
									label={t("detail/measures")}
									options={measureOptions}
									isLoading={isLoading}
									rules={{ required: t("dialog good flow/required") }}
								/>
							</Box>
						</Stack>
					</Stack>

					{fields.map((field, index) => (
						<Stack key={field.id} direction="row" spacing={2}>
							<IconButton onClick={() => remove(index)}>
								<DeleteIcon />
							</IconButton>

							<FormTextField
								name={`goodsFlow.${index}.year` as Path<DialogFormValues>}
								form={form}
								label={t("detail/year")}
								isTypeNumber
								rules={{
									required: t("dialog good flow/required"),
									validate: (v) => validateYear(String(v ?? ""), index),
								}}
								isLoading={isLoading}
							/>

							<FormTextField
								name={`goodsFlow.${index}.value` as Path<DialogFormValues>}
								form={form}
								label={t("detail/value")}
								isTypeNumber
								rules={{ required: t("dialog good flow/required") }}
								isLoading={isLoading}
							/>

							<TextField disabled size="small" value={measureLabel} sx={{ width: 120 }} />
						</Stack>
					))}

					<Stack direction="row" justifyContent="flex-end" spacing={2}>
						<Button
							variant="outlined"
							startIcon={<AddIcon />}
							disabled={saving}
							onClick={() => append({ year: "", value: "" })}
						>
							{t("detail/add flow")}
						</Button>

						<Button
							variant="contained"
							startIcon={
								saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon color="inherit" />
							}
							disabled={!canSave || saving}
							onClick={form.handleSubmit(handleSave)}
						>
							{t("detail/save flows")}
						</Button>
					</Stack>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
